import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, BelegLayout, Vorgangsart } from "@kundeninfo/api-client";
import { darfAbbrechen, Modal, ModalFuss } from "@kundeninfo/ui";
import { Auswahl, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "./meHuellen.js";
import "./BelegLayout.css";

/**
 * DIE LAYOUTWAHL EINES BELEGS (Spec „Layout-Varianten", 21.10.2026, Oberfläche › Am Beleg; Frage 263; LV8) — EIN
 * Baustein für Angebot, Auftrag und Rechnung: der Knopf „Layout …" in der Aktionszeile neben Vorschau und PDF (deren
 * Blatt das Layout bestimmt) und ein kleines striktes Modal. „Übernehmen" ist dort die Bestätigung (Dirk 216c); es
 * schreibt einen eigenen Weg (`setBelegLayout`) mit der Version DER MASKE und meldet die neue Version an den Rahmen —
 * sonst liefe das nächste Speichern der Maske in eine 409.
 *
 * Warum die Version der Maske und nicht die frisch gelesene (Review Task 9, M1, Entscheid R7): Die neue Version wird
 * auf die Felder der Maske gestempelt. Liefe der PUT gegen einen frischeren Stand als den der Maske, hielte die Maske
 * danach alte Felder mit neuer Version, und ihr nächstes „Bearbeiten → Speichern" überschriebe die fremde Änderung
 * still (dasselbe Muster wie A10-1/A10-2 in AuftragDetail und `version` an `Anhaenge`, K10). Liest das Modal beim
 * Öffnen eine andere Version als die der Maske, ist der Stand fremd: Hinweis, „Übernehmen" gesperrt, die Maske lädt
 * neu (`onNeuLaden`); dasselbe nach einer 409.
 *
 * Er lädt erst beim Öffnen: Die Belegliste zeichnet je Eintrag eine Aktionszeile, und ein Abruf je Zeile wäre Last,
 * die niemand ansieht. Die Regeln (wer offen ist, was wählbar ist) entscheidet der Server (`offen`, `auswahl`); hier
 * steht nur die Darstellung. Block `blay__`.
 */

export type BelegLayoutClient = Pick<ApiClient, "getBelegLayout" | "setBelegLayout">;

export type BelegLayoutKnopfProps = {
  client: BelegLayoutClient;
  art: Vorgangsart;
  customerId: string;
  vorgangId: string;
  /** Am Rahmen läuft etwas (Bearbeiten, Status, PDF) — der Knopf wartet. */
  gesperrt: boolean;
  /** Die Version des Belegs, wie die MASKE sie hält — mit ihr schreibt „Übernehmen". */
  version: number;
  /** Der Neuladeweg der Maske (derselbe wie nach ihrer eigenen 409) — bei fremdem Stand. */
  onNeuLaden: () => void | Promise<unknown>;
  /** Die Version des Belegs nach „Übernehmen". */
  onVersion: (version: number) => void;
};

/** Womit der Beleg derzeit rendert — der Satz unter der Auswahl. */
export function wirksamText(stand: BelegLayout): string {
  const w = stand.wirksam;
  if (w === null) return "Für dieses Unternehmen gibt es kein Layout — der Beleg rendert im Systemlayout.";
  if (!w.freigegeben) return `„${w.name}“ ist noch nicht freigegeben — der Beleg rendert im Systemlayout.`;
  return `Derzeit rendert der Beleg mit „${w.name}“${w.archiviert ? " (archiviert)" : ""}.`;
}

export function BelegLayoutKnopf(p: BelegLayoutKnopfProps): ReactElement {
  const [offen, setOffen] = useState(false);
  return (
    <>
      <KiKnopf deaktiviert={p.gesperrt} onClick={() => setOffen(true)}>Layout …</KiKnopf>
      {offen && <BelegLayoutDialog {...p} onSchliessen={() => setOffen(false)} />}
    </>
  );
}

function BelegLayoutDialog(p: BelegLayoutKnopfProps & { onSchliessen: () => void }): ReactElement {
  const { client, art, customerId, vorgangId } = p;
  const [stand, setStand] = useState<BelegLayout | null>(null);
  const [wahl, setWahl] = useState("");
  const [meldung, setMeldung] = useState<string | null>(null);
  /** Der Befund des Servers am Feld `layoutId` (422 `BELEG_LAYOUT_UNGUELTIG`) — steht an der Auswahl. */
  const [feldFehler, setFeldFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /** Ein Abruf läuft — bis zu seinem Ende ist „Übernehmen" zu (sonst liefe ein zweiter Klick nach der 409 noch
   *  einmal gegen den alten Stand). */
  const [laedt, setLaedt] = useState(true);
  const [neuLaden, setNeuLaden] = useState(0);
  /** Für welche Version der Maske schon neu geladen wurde (Maske bzw. Modal) — je Version höchstens einmal. */
  const maskeAngefordert = useRef<number | null>(null);
  const modalAngefordert = useRef<number | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    setLaedt(true);
    client.getBelegLayout(art, customerId, vorgangId)
      .then((s) => { if (!abgebrochen) { setStand(s); setWahl(s.layoutId ?? ""); setFeldFehler(null); } })
      .catch((f: unknown) => {
        if (!abgebrochen) {
          setMeldung(f instanceof ApiError ? f.message : "Die Layoutwahl konnte nicht geladen werden.");
        }
      })
      .finally(() => { if (!abgebrochen) setLaedt(false); });
    return () => { abgebrochen = true; };
  }, [client, art, customerId, vorgangId, neuLaden]);

  function maskeNeuLaden(): void {
    if (maskeAngefordert.current === p.version) return;
    maskeAngefordert.current = p.version;
    void Promise.resolve(p.onNeuLaden()).catch(() => undefined);
  }

  /** Fremder Stand: Modal und Maske kennen nicht dieselbe Version. Der Ältere lädt nach. */
  const veraltet = stand !== null && stand.version !== p.version;
  useEffect(() => {
    if (stand === null || stand.version === p.version) return;
    if (stand.version > p.version) {
      maskeNeuLaden();
    } else if (modalAngefordert.current !== p.version) {
      modalAngefordert.current = p.version;
      setNeuLaden((n) => n + 1);
    }
    // maskeNeuLaden liest nur p.version und p.onNeuLaden — der Lauf hängt an den beiden Versionen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stand, p.version]);

  const geaendert = stand !== null && wahl !== (stand.layoutId ?? "");
  const vorgabe = stand?.auswahl.find((a) => a.vorgabe) ?? null;
  // Die Vorgabe steht als „Vorgabe des Unternehmens" (leere Wahl) — als eigener Eintrag nur, wenn der Beleg sie
  // ausdrücklich gespeichert hat (festgeschrieben).
  const varianten = stand === null ? [] : stand.auswahl.filter((a) => !a.vorgabe || a.id === stand.layoutId);

  async function uebernehmen(): Promise<void> {
    if (stand === null || !geaendert || laeuft || laedt || veraltet) return;
    setLaeuft(true);
    setMeldung(null);
    setFeldFehler(null);
    try {
      const neu = await client.setBelegLayout(art, customerId, vorgangId,
        { version: p.version, layoutId: wahl === "" ? null : wahl });
      p.onVersion(neu.version);
      p.onSchliessen();
    } catch (f) {
      if (f instanceof ApiError && f.status === 409) {
        setMeldung("Der Beleg wurde inzwischen geändert — der aktuelle Stand ist geladen. Bitte erneut wählen.");
        setNeuLaden((n) => n + 1);
        maskeNeuLaden();
      } else {
        const amFeld = f instanceof ApiError && f.status === 422
          ? f.fields?.find((e) => e.field === "layoutId") : undefined;
        if (amFeld !== undefined) setFeldFehler(amFeld.message);
        else setMeldung(f instanceof ApiError ? f.message : "Die Layoutwahl konnte nicht gespeichert werden.");
      }
    } finally {
      setLaeuft(false);
    }
  }

  async function abbrechen(): Promise<void> {
    if (laeuft) return;
    if (await darfAbbrechen(geaendert)) p.onSchliessen();
  }

  return (
    <Modal titel="Layout des Belegs" onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={laeuft}>
      {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
      {meldung === null && veraltet && (
        <Hinweis art="warnung">Der Beleg wurde inzwischen geändert — der aktuelle Stand wird geladen.</Hinweis>
      )}
      {stand === null
        ? (meldung === null && <p className="blay__hinweis" role="status">Lädt …</p>)
        : (
          <div className="blay">
            {stand.festHinweis !== null && <Hinweis art="info">{stand.festHinweis}</Hinweis>}
            <Auswahl beschriftung="Layout" wert={wahl} gesperrt={!stand.offen || laeuft}
              {...(feldFehler === null ? {} : { fehler: feldFehler })}
              onGeaendert={(d) => { setWahl(d.wert); setMeldung(null); setFeldFehler(null); }}>
              <option value="">{`Vorgabe des Unternehmens (${vorgabe === null ? "Systemlayout"
                : `„${vorgabe.name}“`})`}</option>
              {varianten.map((a) => (
                <option key={a.id} value={a.id}>{a.archiviert ? `${a.name} (archiviert)` : a.name}</option>
              ))}
            </Auswahl>
            <p className="blay__hinweis">{wirksamText(stand)}</p>
          </div>
        )}
      {stand !== null && stand.offen
        ? (
          <ModalFuss speichernText="Übernehmen" speichernGesperrt={!geaendert || laeuft || laedt || veraltet}
            abbrechenGesperrt={laeuft}
            onSpeichern={() => { void uebernehmen(); }} onAbbrechen={() => { void abbrechen(); }} />
        )
        : <ModalFuss speichernText="Schließen" nurSchliessen onSpeichern={p.onSchliessen} onAbbrechen={p.onSchliessen} />}
    </Modal>
  );
}
